import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowRight, Loader2, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Logo
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';

export default function SetPassword() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  const isNewAccount = searchParams.get('new_account') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('رابط غير صالح');
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/validate-reset-token/${token}`);
        if (response.data.valid) {
          setValidToken(true);
          setUserInfo(response.data.user);
        } else {
          setTokenError(response.data.message || 'الرابط غير صالح أو منتهي الصلاحية');
        }
      } catch (err) {
        setTokenError(err.response?.data?.detail || 'الرابط غير صالح أو منتهي الصلاحية');
      } finally {
        setLoading(false);
      }
    };
    
    validateToken();
  }, [token]);

  // Password validation — strong policy for admin accounts
  // ascii-only + length 12 + upper + lower + digit + symbol
  const isAscii = (p) => /^[\x20-\x7E]*$/.test(p);
  const passwordRequirements = [
    { id: 'length', label: language === 'ar' ? '12 حرف على الأقل' : 'At least 12 characters', check: (p) => p.length >= 12 },
    { id: 'english', label: language === 'ar' ? 'بالإنجليزية فقط (بدون عربي)' : 'English only (no Arabic)', check: (p) => p.length > 0 && isAscii(p) },
    { id: 'uppercase', label: language === 'ar' ? 'حرف كبير (A-Z)' : 'Uppercase letter (A-Z)', check: (p) => /[A-Z]/.test(p) },
    { id: 'lowercase', label: language === 'ar' ? 'حرف صغير (a-z)' : 'Lowercase letter (a-z)', check: (p) => /[a-z]/.test(p) },
    { id: 'number', label: language === 'ar' ? 'رقم (0-9)' : 'Number (0-9)', check: (p) => /[0-9]/.test(p) },
    { id: 'symbol', label: language === 'ar' ? 'رمز خاص (!@#$%...)' : 'Special character (!@#$%...)', check: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?~`]/.test(p) },
  ];

  const allRequirementsMet = passwordRequirements.every(req => req.check(password));
  const passwordsMatch = password === confirmPassword && password.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      toast.error('كلمة المرور لا تستوفي الشروط المطلوبة');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('كلمتا المرور غير متطابقتين');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/set-password`, {
        token,
        new_password: password
      });
      
      setSuccess(true);
      toast.success('تم تعيين كلمة المرور بنجاح');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'حدث خطأ أثناء تعيين كلمة المرور');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#c9a227] mx-auto mb-4" />
          <p className="text-[#808080]">جاري التحقق من الرابط...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!validToken) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center"
        >
          <div className="bg-red-500/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <XCircle className="h-10 w-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">رابط غير صالح</h1>
          <p className="text-[#808080] mb-8">{tokenError}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#c9a227] hover:text-[#d4af37] transition-colors"
          >
            <ArrowRight className="h-4 w-4" />
            العودة للصفحة الرئيسية
          </Link>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="bg-emerald-500/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">تم تعيين كلمة المرور بنجاح!</h1>
          <p className="text-[#808080] mb-8">
            يمكنك الآن تسجيل الدخول باستخدام اسم المستخدم وكلمة المرور الجديدة.
          </p>
          {userInfo?.username && (
            <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
              <p className="text-[#808080] text-sm mb-2">اسم المستخدم الخاص بك:</p>
              <p className="text-[#c9a227] font-mono text-lg">{userInfo.username}</p>
            </div>
          )}
          <Button
            onClick={() => navigate('/xK9mP2vL8qR4wN6j')}
            className="w-full bg-[#c9a227] hover:bg-[#d4af37] text-black h-12 font-semibold"
          >
            الانتقال لصفحة تسجيل الدخول
          </Button>
        </motion.div>
      </div>
    );
  }

  // Set password form
  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4 py-12">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full relative z-10"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#808080] hover:text-white transition-colors"
          >
            <ArrowRight className="h-4 w-4" />
            العودة للصفحة الرئيسية
          </Link>
          <LanguageToggle />
        </div>

        {/* Logo & Title */}
        <div className="text-center mb-8">
          <img
            src={LOGO_TRIANGLE}
            alt="ASB606"
            className="h-16 w-auto mx-auto mb-4 drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
          />
          <h1 className="text-2xl font-bold text-white mb-2">
            {isNewAccount ? 'مرحباً بك!' : 'إعادة تعيين كلمة المرور'}
          </h1>
          <p className="text-[#808080]">
            {isNewAccount 
              ? 'قم بتعيين كلمة المرور الخاصة بك للبدء'
              : 'أدخل كلمة المرور الجديدة'
            }
          </p>
        </div>

        {/* User Info */}
        {userInfo && (
          <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#c9a227]/20 flex items-center justify-center">
                <KeyRound className="h-5 w-5 text-[#c9a227]" />
              </div>
              <div>
                <p className="text-white font-medium">{userInfo.name || userInfo.email}</p>
                {userInfo.username && (
                  <p className="text-xs text-[#808080]">اسم المستخدم: {userInfo.username}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#808080] mb-2">
                كلمة المرور الجديدة
              </label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#606060]" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10 pl-10 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#606060] focus:border-[#c9a227]"
                  placeholder="أدخل كلمة المرور"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606060] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            {password.length > 0 && (
              <div className="space-y-2 bg-white/5 rounded-lg p-3">
                {passwordRequirements.map(req => (
                  <div key={req.id} className="flex items-center gap-2 text-sm">
                    {req.check(password) ? (
                      <CheckCircle className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )}
                    <span className={req.check(password) ? 'text-emerald-400' : 'text-[#808080]'}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-[#808080] mb-2">
                تأكيد كلمة المرور
              </label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#606060]" />
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pr-10 pl-10 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#606060] focus:border-[#c9a227]"
                  placeholder="أعد إدخال كلمة المرور"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606060] hover:text-white transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {confirmPassword.length > 0 && (
                <div className="flex items-center gap-2 mt-2 text-sm">
                  {passwordsMatch ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-emerald-400" />
                      <span className="text-emerald-400">كلمتا المرور متطابقتان</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 text-red-400" />
                      <span className="text-red-400">كلمتا المرور غير متطابقتين</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting || !allRequirementsMet || !passwordsMatch}
              className="w-full bg-[#c9a227] hover:bg-[#d4af37] text-black h-12 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin ml-2" />
                  جاري الحفظ...
                </>
              ) : (
                'تعيين كلمة المرور'
              )}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
